import React, { FunctionComponent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Set } from 'immutable';
import { Link, useHistory } from 'react-router-dom';
import useFavorite from '../../../../hooks/useFavorite';
import { useIsReadOnly } from '../../../../hooks/useIsReadOnly';
import { useAuthUserHasPermission } from '../../../../hooks/useAuthUserHasPermission';
import { ALL_DEVICES_TAG } from '../../../../shared/helpers';
import { UserCan } from '../../../../shared/constants';
import { iFullStoreState, ItemType } from '../../../../shared/interfaces';
import Dialog, { DialogConfigSetter } from '../../../Dialog';
import { TagsListItemProps } from './interfaces';
import styles from './TagsListItem.module.scss';
import { ReactComponent as StarFill } from '../../../../assets/svg/star_fill_w16.svg';
import { ReactComponent as StarBorder } from '../../../../assets/svg/star_border_w16.svg';
// import { ReactComponent as DeleteIcon } from '../../../../assets/svg/delete_outline.svg';
import { ReactComponent as EditIcon } from '../../../../assets/svg/edit_w16.svg';
import { ReactComponent as BellIcon } from '../../../../assets/svg/notification_16w.svg';
import { ReactComponent as ReportIcon } from "../../../../assets/svg/list_alt.svg";
import { Tooltip } from '@mui/material';
import { removeReportTrips } from '../../../../stores/reducers/devicesTripsPoints/AC';
import { ReportAC } from '../../../../stores/reducers/report-reducers';

const TagsListItem: FunctionComponent<TagsListItemProps> = ({
  details,
  id,
  isViewed,
  isTagType,
  deleteItem,
  toggleTag,
  index
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const tagDevicesIdsSet = useSelector<iFullStoreState, Set<string>>((s) => s.tagsDevicesMap.tagsDevices.get(id, Set<string>()));

  // option configuration
  const isAllDeviceTag = (id === ALL_DEVICES_TAG);
  const canEditTags = useAuthUserHasPermission(UserCan.TAG_THINGS);
  const isReadOnly = useIsReadOnly(id, ItemType.tag);
  const hideOption = !canEditTags || isReadOnly;

  // tag name
  const displayName = details.name;
  const tagLink = isTagType ? `/tag/${id}` : '';
  const canNavigateToTag = Boolean(tagLink) && !hideOption;
  const shouldShowLinkForTag = canNavigateToTag;

  // favorite
  const {
    toggle: handleFavoriteClick,
    isFavorite,
  } = useFavorite({ type: ItemType.tag, id });
  const Star = isFavorite ? StarFill : StarBorder;

  // device count
  const deviceCount = tagDevicesIdsSet.size;

  // dialog window
  const dialogRef = React.useRef<DialogConfigSetter>();
  const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();

  // Options logic
  // view
  const handleViewTag = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    toggleTag(id);
  };

  // delete
  // const shouldShowDelete = !(!deleteItem || hideOption);
  // const handleDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
  //   e.preventDefault();

  //   if (deleteItem) {
  //     const dialog = dialogRef.current;

  //     // check confirm
  //     const confirmed = await dialog?.({
  //       type: 'CONFIRM',
  //       title: 'Warning!',
  //       body: (
  //         <div>
  //           <p>This will delete <b>{displayName}</b> along with any associated geoFences or settings.</p>
  //           <p>Click OK to confirm.</p>
  //         </div>
  //       ),
  //     });

  //     // go out if user cancel confirmation
  //     if (!confirmed) return;

  //     deleteItem(id);
  //   }
  // }

  const handleOpenInventoryReportForTag = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    dispatch(removeReportTrips());
    dispatch({ type: 'REPORT_RESET_ALL_FILTERS' });

    if (!isAllDeviceTag) dispatch(ReportAC.TOGGLE_ITEM_FILTER('tag', id));
    history.push('/reports/new/inventory');
  }

  const showBell = details.isAlertType;

  const ItemContainer = useMemo(() => (
    ({ children, ...props }: any) => (
      shouldShowLinkForTag ? (
        <Link to={tagLink} {...props}>{children}</Link>
      ) : (
        <div {...props}>{children}</div>
      )
    )
  ), [shouldShowLinkForTag, tagLink]);

  const showEditOption = (canNavigateToTag && !isAllDeviceTag);
  const showInventoryReportOption = true;

  const options = [
    {
      key: 'view',
      shouldRender: isTagType,
      isHidden: false,
      element: (
        <Tooltip title='View On Map' placement='top'>
          <button
            type='button'
            onClick={handleViewTag}
            className={styles.optionItem}
          >
            <span className={classNames(styles.viewText, { [styles.viewText_active]: isViewed })}>
              {isViewed ? 'Hide' : 'View'}
            </span>
          </button>
        </Tooltip>
      )
    },
    {
      key: 'edit',
      shouldRender: true,
      isHidden: !showEditOption,
      element: (
        <Tooltip title='Edit Tag' placement='top'>
          <Link
            to={tagLink}
            className={styles.optionItem}
          >
            <EditIcon className={styles.optionIcon} />
          </Link>
        </Tooltip>
      )
    },
    {
      key: 'inventoryReport',
      shouldRender: true,
      isHidden: !showInventoryReportOption,
      element: (
        <Tooltip title='View Inventory Report' placement='top'>
          <button
            type='button'
            onClick={handleOpenInventoryReportForTag}
            className={styles.optionItem}
          >
            <ReportIcon
              className={classNames(styles.optionIcon, styles.optionIconOrange)}
            />
          </button>
        </Tooltip>
      )
    },
    // {
    //   key: 'delete',
    //   shouldRender: shouldShowDelete,
    //   element: (
    //     <Tooltip title='Delete Tag' placement='top'>
    //       <button
    //         type='button'
    //         onClick={handleDelete}
    //         className={styles.optionItem}
    //       >
    //         <DeleteIcon
    //           className={styles.optionIcon}
    //         />
    //       </button>
    //     </Tooltip>)
    // }
  ];

  const renderOptions = options.filter(option => option.shouldRender);

  const pinColor = details.color ?? '';
  const hasColor = pinColor && pinColor !== '';

  return (
    <div className={styles.tagsListItemWrapper}>
      <Dialog setupConfig={setupDialog} />

      <ItemContainer className={classNames(styles.tagsListItem,
        {
          [styles.tagsListItem_withThreeColumns]: isTagType,
          [styles.tagsListItem_shownOnMap]: isViewed,
        })}
      >
        <div className={styles.tag}>
          {!isAllDeviceTag && (
            <span
              className={styles.pinIcon}
              style={{ color: hasColor ? pinColor : '#000' }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className={styles.pinSvg}>
                <path
                  fill={hasColor ? 'currentColor' : 'none'}
                  stroke={hasColor ? 'none' : 'currentColor'}
                  strokeWidth={hasColor ? 0 : 1.5}
                  d="M12 2c-4.1 0-7.5 3.3-7.5 7.4 0 5.6 7.5 12.6 7.5 12.6S19.5 15 19.5 9.4C19.5 5.3 16.1 2 12 2z"
                />
              </svg>
            </span>
          )}
          {showBell && <BellIcon className={styles.bellIcon} />}

          <p className={styles.tagName}>
            {displayName}
          </p>

          <button
            type='button'
            onClick={(e) => {
              e.preventDefault();
              handleFavoriteClick();
            }}
            className={classNames(styles.favoriteButton, { [styles.favorite]: isFavorite })}
          >
            <Star className={styles.favoriteStar} />
          </button>
        </div>

        {/* Device */}
        {/* Don`t show device count if it's all device tag */}
        {isTagType && (<p className={styles.device}>{isAllDeviceTag ? '' : deviceCount}</p>)}

        <div
          className={styles.options}
        >
          {renderOptions.map((option) => {
            if (!option.shouldRender) return null;

            const style = option.isHidden ? { visibility: 'hidden', pointerEvents: 'none' } : {};
            return React.cloneElement(option.element, {
              key: option.key,
              style: style,
              className: `${styles.optionItem}`
            })
          })}
        </div>
      </ItemContainer>
    </div>
  );
};

export default TagsListItem;